import React, { useEffect } from "react"
import { batch, useSignal } from "@preact/signals-react"

import {
	directSiteBrokerProvider,
	mainProvider,
	mainSite,
	offices,
	reports,
	searchID,
	sites
} from "@signals"
import AnalyzerCommonFilters from "Pages/AnalyzersV2/Filters"
import { useAnalyzerPositionFilters } from "Pages/AnalyzersV2/Position/@hooks"
import { positionSchema } from "./schema"


export default function PositionFilters() {
	const { onSubmit, isValid, errors } = useAnalyzerPositionFilters()

	const isProviderMode = !!directSiteBrokerProvider.value
	const hasContext = !!offices.value && !!reports.value || searchID.value
	// One automatic mode switch per context. Both families can come back empty, and
	// without the guard they would bounce the toggle against each other forever;
	// it also keeps a manual return to an empty mode from being undone instantly.
	const autoSwitchDone = useSignal(false)

	// Broker is the default read of this analyzer — rank a provider inside one
	// non-direct web — but only on arrival. From there the mode is the user's
	// choice, and only an empty family moves it.
	useEffect(() => {
		directSiteBrokerProvider.value = true
	}, [])

	// A new execution drops the reference brand, which belonged to the previous
	// one. Changing the office does not: those selectors refetch scoped to the new
	// office and drop the value themselves if it is not available there, which
	// beats making the user pick it again on every office.
	useEffect(() => {
		batch(() => {
			mainSite.value = ''
			mainProvider.value = ''
			sites.value = null
			autoSwitchDone.value = false
		})
	}, [reports.value, searchID.value])

	// A different office can have a different set of webs on each side, so the
	// one-shot switch is armed again — without touching the reference brand.
	useEffect(() => {
		autoSwitchDone.value = false
	}, [offices.value])

	// Switching mode swaps the site selector between single and multiple, and the
	// two families of sites (direct vs broker) do not overlap, so the previous
	// selection is dropped — as the empty value of the new shape.
	useEffect(() => {
		sites.value = isProviderMode ? null : []
	}, [directSiteBrokerProvider.value])

	// `directSiteBrokerProvider` is shared with the Brokers analyzer, which never
	// resets it on mount. Position is the only one defaulting it to `true`, so it
	// restores the signal's declared default on the way out.
	useEffect(() => () => {
		directSiteBrokerProvider.value = false
	}, [])

	return <AnalyzerCommonFilters
		components={{
			singleDate: false,
			// The Web / Web & Provider switch drives `groupBySiteProvider`, which
			// this analyzer ignores — showing it would suggest a choice that
			// changes nothing.
			webProviderToggle: false,
			directSiteBrokerProviderToggle: true,
			lengthRental: hasContext,
			mainSite: !isProviderMode && hasContext,
			mainProvider: isProviderMode && hasContext,
		}}
		compProps={{
			directSiteBrokerProviderToggle: { falseText: 'Direct Sites', trueText: 'Broker' },
			lengthRental: { grid: { xs: 1 }, autoSelect: true },
			dates: { grid: { xs: 3 }, multiple: true, autoSelect: false, disableClearable: false },
			// Comparing brands within one broker: the reference is a provider, so
			// the site scopes it to a single non-direct web.
			// Comparing direct webs: the reference is a direct site and the selector
			// narrows the set of direct webs, optionally and with several values.
			mainSite: { filters: { getDirectSites: true } },
			sites: {
				multiple: !isProviderMode,
				// The reset effect only runs after this render, so on the render that
				// follows a mode switch the signal still holds the previous shape.
				// MUI throws if a multiple Autocomplete gets a non-array, so the value
				// is coerced to what the selector expects right now.
				value: isProviderMode
					? (Array.isArray(sites.value) ? null : sites.value ?? null)
					: (Array.isArray(sites.value) ? sites.value : []),
				filters: { getDirectSites: !isProviderMode },
				onFinishAjax: options => {
					if (options?.length) {
						// Broker mode ranks the provider inside one web, so the site is
						// picked first and every dependent selector is then scoped by it.
						// Same behaviour as the Brokers analyzer, but tolerating an empty
						// list. Direct Sites narrows an optional set, so nothing is forced.
						if (isProviderMode) {
							sites.value = options[0].value
						}

						return
					}

					// This execution has no webs on this side. The analyzer still works
					// against the other family, so it switches over instead of leaving a
					// dead selector blocking the whole page.
					batch(() => {
						sites.value = isProviderMode ? null : []
						if (!autoSwitchDone.value) {
							autoSwitchDone.value = true
							directSiteBrokerProvider.value = !isProviderMode
							// as the manual switch does: the other mode's reference goes, so the new field starts
							// empty and takes the account's own brand / site when its list loads (QA CU-869fmm949 risk 4)
							mainSite.value = ''
							mainProvider.value = ''
						}
					})
				},
			},
		}}
		errors={errors}
		onSubmit={onSubmit}
		isValid={isValid}
		schema={positionSchema}
	/>
}
